import { X } from "lucide-react";
import Dialog from "@imminently/decisively-ui/Dialog";
import Button from "@imminently/decisively-ui/Button";
import SidebarNav from "@imminently/decisively-ui/SidebarNav";
import { useAuth } from "@/legacy/redux";
import { PreferencesProvider, usePreferences, type PreferencesTab } from "./PreferencesContext";
import { AppearancePanel } from "./panels/AppearancePanel";
import { ExperimentalPanel } from "./panels/ExperimentalPanel";
import { DeveloperPanel } from "./panels/DeveloperPanel";
import { DesignPanel } from "./panels/DesignPanel";
import { LegacyPagesPanel } from "./panels/LegacyPagesPanel";

const SHOW_APPEARANCE_TAB = false;

const preferenceSections: Array<{ id: string; label: string; items: Array<{ id: PreferencesTab; label: string }> }> = [
  {
    id: "settings",
    label: "Settings",
    items: [
      ...(SHOW_APPEARANCE_TAB ? [{ id: "appearance" as const, label: "Appearance" }] : []),
    ],
  },
  {
    id: "features",
    label: "Features",
    items: [
      { id: "experimental", label: "Experimental" },
      { id: "legacy_pages", label: "Legacy Pages" },
      { id: "developer", label: "Developer" },
      { id: "design", label: "Design" },
    ],
  },
];

const visiblePreferenceSections = preferenceSections.filter((section) => section.items.length > 0);

const UserIdentity = () => {
  const { user } = useAuth();
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return (
    <div className="flex items-center gap-3 px-3 py-3 mb-1">
      <div className="overflow-hidden">
        {fullName && <p className="text-sm font-semibold truncate">{fullName}</p>}
        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
      </div>
    </div>
  );
};

const ActivePanel = () => {
  const { activeTab } = usePreferences();
  switch (activeTab) {
    case "appearance":
      return <AppearancePanel />;
    case "experimental":
      return <ExperimentalPanel />;
    case "legacy_pages":
      return <LegacyPagesPanel />;
    case "developer":
      return <DeveloperPanel />;
    case "design":
      return <DesignPanel />;
  }
};

const PreferencesContent = ({ onClose }: { onClose: () => void }) => (
  <div className="flex h-full overflow-hidden relative">
    {/* Close button — top right */}
    <Button icon
      label="Close preferences"
      onClick={onClose}
      className="absolute top-2 right-2 z-10"
      size="md"
    >
      <X />
    </Button>

    <PreferencesSidebar />

    {/* Right content area */}
    <div className="flex-1 overflow-y-auto">
      <ActivePanel />
    </div>
  </div>
);

const PreferencesSidebar = () => {
  const { activeTab, setActiveTab } = usePreferences();

  return (
    <SidebarNav
      collapsed={false}
      width={224}
      theme="light"
    >
      <SidebarNav.Panel className="border-r border-border">
        <SidebarNav.Header>
          <UserIdentity />
        </SidebarNav.Header>
        <SidebarNav.Content>
          {visiblePreferenceSections.map((section) => (
            <SidebarNav.Group key={section.id}>
              <SidebarNav.GroupLabel>{section.label}</SidebarNav.GroupLabel>
              <SidebarNav.GroupContent>
                <SidebarNav.Menu>
                  {section.items.map((item) => (
                    <SidebarNav.MenuItem key={item.id}>
                      <SidebarNav.MenuButton
                        isActive={activeTab === item.id}
                        onClick={() => setActiveTab(item.id)}
                      >
                        <span>{item.label}</span>
                      </SidebarNav.MenuButton>
                    </SidebarNav.MenuItem>
                  ))}
                </SidebarNav.Menu>
              </SidebarNav.GroupContent>
            </SidebarNav.Group>
          ))}
        </SidebarNav.Content>
      </SidebarNav.Panel>
    </SidebarNav>
  );
};

export interface PreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: PreferencesTab;
}

const getInitialTab = (initialTab?: PreferencesTab): PreferencesTab => {
  if (initialTab === "appearance" && !SHOW_APPEARANCE_TAB) return "experimental";
  if (initialTab === "sidebar") return "experimental";
  return initialTab ?? (SHOW_APPEARANCE_TAB ? "appearance" : "experimental");
};

export const PreferencesModal = ({ open, onOpenChange, initialTab }: PreferencesModalProps) => (
  <Dialog open={open} onOpenChange={onOpenChange} intent="cover">
    <Dialog.Content style={{ padding: 0, overflow: "hidden" }}>
      <PreferencesProvider initialTab={getInitialTab(initialTab)}>
        <PreferencesContent onClose={() => onOpenChange(false)} />
      </PreferencesProvider>
    </Dialog.Content>
  </Dialog>
);
